import React from 'react';
import SidebarLayout from '../../dashboard/components/SidebarLayout';
import {StoreMapRefactored} from '../../../Presentation/components/common/StoreMapRefactored';
import CityMapControls from '../../../Presentation/components/common/CityMapControls';
import StoreDetails from '../../../Presentation/components/common/StoreDetails';
import {useDashboardManagement} from '../../dashboard/hooks/useDashboardManagement';
import {Store, AlertCircle} from 'lucide-react';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

const StoresMapPage: React.FC = () => {
  const {hasPermission} = useAuthorization();

  // Unified dashboard management
  const {
    // Data
    user, isProvider, brandNames,
    selectedStore,
    selectedStoreCount,
    currentStoresError
  } = useDashboardManagement();

  return (
    <SidebarLayout>
      <div className="h-full flex flex-col gap-6">
        {/* Provider Info Banner */}
        {isProvider && (
          <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
                <span className="text-brand-600 font-semibold text-sm">🗺️</span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-brand-900">
                  خريطة المتاجر - {user?.username || ''}
                </h3>
                <p className="text-xs text-brand-700">
                  البراندات المرتبطة: {brandNames.length > 0 ? brandNames.join(', ') : 'لا توجد براندات مرتبطة'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Store Info Banner */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center">
              <Store className="w-4 h-4 text-brand-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900">
                المتاجر
              </h3>
              <p className="text-xs text-gray-600">
                {selectedStoreCount > 0 ? `${selectedStoreCount} متجر محدد` : 'لا يوجد متاجر محددة'}
              </p>
            </div>
          </div>
        </div>

        {/* Error Messages */}
        {currentStoresError && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">
              خطأ في تحميل المتاجر: {String(currentStoresError)}
            </span>
          </div>
        )}

        {/* No Access Message */}
        {!hasPermission(PERMISSIONS.MANAGE_STORES) && (
          <PermissionDeniedCard
            title="لا توجد صلاحيات للمتاجر"
            message="ليس لديك صلاحيات للوصول إلى خريطة المتاجر. يرجى التواصل مع المدير للحصول على الصلاحيات المناسبة."
          />
        )}

        {/* Main Content - Map Interface */}
        {hasPermission(PERMISSIONS.MANAGE_STORES) && (
          <div className="flex-1 flex flex-col lg:flex-row gap-6">
            {/* Left Column - Map and Controls */}
            <div className="flex-1 flex flex-col space-y-4">
              {/* City Map Controls */}
              <CityMapControls />

              {/* Map */}
              <div className="flex-1">
                <StoreMapRefactored viewMode="stores" />
              </div>
            </div>

            {/* Right Column - Store Details */}
            <div className="w-full lg:w-[400px] min-h-[400px] lg:min-h-full">
              <StoreDetails store={selectedStore} />
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default StoresMapPage;
